import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, distinctUntilChanged, filter, firstValueFrom, takeUntil } from 'rxjs';

import { BulkMaterials } from '../../../shared/models/assays.model';
import { ReportContentItem, ReportContentItemResponse, PatientFilePanelInfo, CalAdjustedDisplayOrder } from '../../../shared/models/run.model';
import { Study } from '../../../shared/models/study.model';
import { patchReportContentItems, setCalAdjustedDisplayOrder, setRmqcAssayCount } from '../../state/actions';
import { selectAppState, selectPanelName, selectPreparedInstrumentSeeds, selectReportContentItemsSlice, selectStudy, selectIsStudySaved } from '../../state/selectors';
import { transformToCamelCase } from '../../../shared/utils/helper';
import { RunTabChange } from '../../../shared/models/run-tab-change.model';
import { ReportContentSeed } from '../../../shared/models/report-content-seed.model';
import { ColumnSelection, ReportItemsImportDialogData } from '../../../shared/models/dialog.model';
import { isInternalQcBeadRegion, isSeededPlaceholderRegion } from '../../../shared/utils/run-info.utils';
import { applyPrintColumnSelection } from '../../../shared/utils/print-column.util';
import { hasOperatorEnteredData, REPORT_ITEM_COLUMNS, REPORT_ITEM_FORM_KEY_BY_COLUMN } from '../../../shared/utils/report-item-columns';
import { ReportItemsImportDialogComponent } from '../../../shared/components/report-items-import-dialog/report-items-import-dialog.component';
import { CommonDialogComponent } from '../../../shared/components/common-dialog/common-dialog.component';
import { AppUtilityService } from '../../../shared/services/app-utility.service';
import {
  ReportItemsExcelService,
  ReportItemsImportContext,
  ReportItemsImportRow
} from '../services/report-items-excel.service';

/** One uploaded row paired with the table row it will overwrite, or null when it will be added. */
interface ReportItemsImportPlanEntry {
  row: ReportItemsImportRow;
  targetIndex: number | null;
}

/** How many rows one kit lot is short of the requested assay count. */
interface AssayCountDeficit {
  /** The lot as the table spells it, for the rows that will be created. */
  lot: string;
  /** The instrument the created rows belong to; empty when the study names none. */
  runId: string;
  /** The normalized lot+instrument pair, for matching against rows already in the table. */
  key: string;
  count: number;
}

/** Everything the table needs to become exactly `count` assay rows per kit lot and instrument. */
interface AssayCountResizePlan {
  removals: number[];
  deficits: AssayCountDeficit[];
}

@Component({
  selector: 'app-run-report-items',
  templateUrl: './run-report-items.component.html',
  styleUrl: './run-report-items.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RunReportItemsComponent implements OnInit, OnDestroy {
  isStudySaved$ = this.store.select(selectIsStudySaved);
  @Input() ownTabIndex = 1;
  @ViewChild('excelSelector') excelSelector?: ElementRef<HTMLInputElement>;
  // *MS Report Items is one of the heaviest tabs because it renders hundreds of controls, so we explicitly track tab
  //   visibility. While hidden we detach change detection and cache incoming NgRx payloads; when the tab becomes active
  //   we replay those payloads exactly once so the user never pays the hydration cost until they actually view the tab.*MS
  @Input() set tabChange(tabChangeEvent: RunTabChange | null) {
    if (!tabChangeEvent) {
      return;
    }

    const becameInactive = tabChangeEvent.previousIndex === this.ownTabIndex &&
      tabChangeEvent.currentIndex !== this.ownTabIndex;
    const becameActive = tabChangeEvent.currentIndex === this.ownTabIndex;

    if (becameInactive && !this.isUpdated) {
      this.scheduleDeferredSave(() => this.storeFormValues());
    }
    if (becameInactive) {
      this.isTabActive = false;
      this.cancelScheduledTabActivation();
      // *MS Suspend change detection while tab content is hidden to avoid re-running heavy templates.*MS
      this.cdr.detach();
      return;
    }
    if (becameActive) {
      this.scheduleTabActivation();
    }
  }
  selectedStudyData$ = this.store.select(selectStudy);
  appState$ = this.store.select(selectAppState);
  panelName$ = this.store.select(selectPanelName);
  selectedStudyData!: Study | null;
  reportContent!: ReportContentItemResponse;
  runIds: string[] = [];
  assayNames: string[] = [];
  beadRegions: number[] = [];
  kitLots: string[] = [];
  /** Number of assay rows to hold per selected kit lot and instrument, for every run type. */
  rmqcAssayCount = 1;
  /**
   * Bound to the Number of Assays input. Held apart from `rmqcAssayCount` so a half-typed value
   * never resizes the table; `applyAssayCount` commits it.
   */
  assayCountInput: number | null = 1;
  bulkMaterial: BulkMaterials[] = [];
  variableColumnMeta: Array<{ id: string; label: string; partNumber: string }> = [];
  assayBeadMapping!: PatientFilePanelInfo;
  private assayRegionLookup = new Map<string, number>();
  variableColumns: string[] = [];
  reportContentFormGroup!: FormGroup;
  reportContentFormArray!: FormArray<FormGroup>;
  reportContentStaticColumns: string[] = ['select', ...REPORT_ITEM_COLUMNS.map(column => column.columnKey)];
  reportContentColumns: string[] = [...this.reportContentStaticColumns];
  reportContentDataSource = new MatTableDataSource<ReportContentItem>([]);
  selection = new SelectionModel<ReportContentItem>(true, []);
  materialResultTypes: string[] = ['Instrument', 'Calculated'];
  isLoading = false;
  isUpdated = true;
  isEmpty = false;
  /** True while an uploaded workbook is being read, so the upload button cannot be re-fired. */
  isImporting = false;
  readonly excelAcceptedTypes = '.xlsx';
  isPrintView = false;
  isPrintMode = false;
  studyName = '';
  specVersion = '';
  appVersion = '';
  tableColumns: ColumnSelection[] = [];
  selectedPrintColumns: ColumnSelection[] = [];
  readonly anchorColumnKeys = ['kitLot', 'runId', 'assayName', 'beadRegion'];
  readonly columnsPerPrintPage = 9;
  private printPagesSnapshot: ColumnSelection[][] = [];
  /** Null until the operator narrows the print selection; see `applyPrintColumnSelection`. */
  private printColumnSelection: ColumnSelection[] | null = null;
  protected destroy$ = new Subject<boolean>();
  private isTabActive = false;
  private pendingReportContent: ReportContentItemResponse | null = null;
  private pendingSelectedStudy: Study | null = null;
  private preparedInstrumentSeeds: ReportContentSeed[] = [];
  private tabActivationHandle: number | ReturnType<typeof setTimeout> | null = null;
  private tabActivationHandleIsTimeout = false;
  private destroyed = false;
  /** Cache the last-saved row signature so we can detect changes without reserializing the full table. */
  private reportContentBaselines = new Map<string, string>();
  private pendingDisplayOrder: string[] | null = null;
  /** Track the active variable column structure to know when metadata changed alongside row edits. */
  private variableColumnsSignature = '';

  constructor(
    private store: Store,
    private fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private router: Router,
    private readonly dialog: MatDialog,
    private readonly excelService: ReportItemsExcelService,
    private readonly appUtilityService: AppUtilityService
  ) {
    this.cdr.detach();
  }

  ngOnInit(): void {
    this.isPrintView = this.router?.url?.includes('print');
    if (this.isPrintView) {
      this.isTabActive = true;
      this.cdr.reattach();
      // The print route is print mode; there is no other reason to be on it. This used to be
      // switched on by the print menu opening over the page, which also built the first page
      // snapshot — `buildPrintColumns` does that now.
      this.isPrintMode = true;
    }

    this.appState$.pipe(
      filter(appState => !!appState),
      takeUntil(this.destroy$)
    ).subscribe(appState => {
      this.studyName = appState.selectedStudy?.studyName ?? '';
      this.specVersion = appState.selectedStudy?.panelSpecs?.revisionNumber ?? '';
      this.appVersion = appState.appVersion ?? '';
    });

    this.selectedStudyData$.pipe(
      filter(selectedStudy => !!selectedStudy),
      distinctUntilChanged((previous, current) =>
        previous?.studyId === current?.studyId &&
        previous?.kitLots === current?.kitLots &&
        previous?.assayNames === current?.assayNames &&
        previous?.beadRegions === current?.beadRegions
      ),
      takeUntil(this.destroy$)
    ).subscribe(selectedStudy => {
      if (!this.isTabActive) {
        // *MS Skip heavy rehydration work until the tab is brought back into view.*MS
        this.pendingSelectedStudy = selectedStudy;
        return;
      }
      this.applySelectedStudyData(selectedStudy);
    });

    this.store.select(selectPreparedInstrumentSeeds)
      .pipe(takeUntil(this.destroy$))
      .subscribe(seeds => {
        // *MS Share precomputed instrument seeds so the tab doesn't rebuild combinations on first entry.*MS
        this.preparedInstrumentSeeds = seeds ?? [];
      });

    this.subscribeToReportContentResponse();
  }

  private applySelectedStudyData(selectedStudy: Study): void {
    // *MS Sync every Study-dependent field (assay lists, bead regions, kit lots, comments, metadata) in one place so
    //   cached payloads can rehydrate quickly. Centralizing this logic ensures that when a hidden tab becomes active,
    //   we hydrate exactly once with the freshest study snapshot instead of replaying multiple partial updates.*MS
    this.pendingSelectedStudy = null;
    this.selectedStudyData = selectedStudy;
    this.kitLots = selectedStudy.kitLots.map(f => String(f)) ?? [];
    this.rmqcAssayCount = selectedStudy.rmqcAssayCount && selectedStudy.rmqcAssayCount > 0 ? selectedStudy.rmqcAssayCount : 1;
    this.assayCountInput = this.rmqcAssayCount;
    this.bulkMaterial = selectedStudy.panelSpecs?.bulkMaterials || [];
    this.assayBeadMapping = this.findPanelAssayMapping(selectedStudy) ?? new PatientFilePanelInfo();
    this.assayRegionLookup = this.buildAssayRegionLookup(selectedStudy);
    this.runIds = Array.from(new Set([
      ...selectedStudy.patientFileMetadata.map(f => f.instrumentId),
      ...selectedStudy.calFileMetadata.map(f => f.instrumentId),
      ...selectedStudy.controlFileMetadata.map(f => f.instrumentId)
    ]));
    this.assayNames = [...selectedStudy.assayNames].sort((a, b) => a.localeCompare(b));
    this.beadRegions = [...selectedStudy.beadRegions].sort((a, b) => a - b);
      if (this.reportContent) {
        this.updateVariableColumnMeta();
        this.loadReportContent();
      }

    console.info('[RunReportItems] applySelectedStudyData', {
      studyId: selectedStudy.studyId,
      runType: selectedStudy.runType,
      kitLotsCount: this.kitLots.length,
      assayNamesCount: this.assayNames.length,
      beadRegionsCount: this.beadRegions.length,
      runIds: this.runIds
    });
  }

  loadReportContent(): void {
    // *MS Rebuilding the entire table is expensive, so we reuse the MatTableDataSource instance and only repopulate
    //   it with already-sorted rows. This also reuses the FormArray controls when possible, preventing Material from
    //   re-registering sort/paginator hooks every time the store emits.*MS
    const reportContentItems = this.reportContent?.reportContentItems ?? [];
    this.reportContentColumns = this.reportContentStaticColumns.concat(this.variableColumns);
    // Rows already on screen keep their displayed order when the store echoes a save back; only a
    // first load (or rows the table has not seen) falls back to the default sort.
    const sortedItems = this.preserveDisplayedOrder(this.sortReportContentItems(reportContentItems));
    this.pendingDisplayOrder = null;
    const defaultResultType = this.getDefaultResultTypeForRunType();
    const defaultedItems = defaultResultType
      ? sortedItems.map(item => ({ ...item, resultType: defaultResultType }))
      : sortedItems;

    this.reportContentFormArray = this.fb.array<FormGroup>([]);
    defaultedItems.forEach((materialItem: ReportContentItem) => {
      this.reportContentFormArray.push(this.createReportContentFormGroup(materialItem));
    });

    this.reportContentFormGroup = this.fb.group({
      reportContent: this.reportContentFormArray
    });

    this.reportContentFormGroup.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.isUpdated = false;
    });

    // *MS Keep the same data source instance to avoid re-registering sort and paginator bindings.*MS
    this.reportContentDataSource.data = defaultedItems;
    const dataTableOrder = this.buildDisplayOrderFromCurrentTable(this.reportContentDataSource.data);
    this.store.dispatch(setCalAdjustedDisplayOrder({ dataTableOrder }));
    this.selection.clear();
    // *MS Snapshot the canonical row signatures so later saves only serialize rows whose values actually changed.*MS
    this.captureReportContentBaselines(defaultedItems);
    this.refreshVariableColumnSignature();

    if (!defaultedItems.length) {
      this.addKitLots();
    } else {
      this.isUpdated = true;
    }

    this.isLoading = false;
    this.isEmpty = false;
    this.syncAssayCountToTable();
    this.buildPrintColumns();

    console.info('[RunReportItems] loadReportContent', {
      reportContentCount: reportContentItems.length,
      defaultedCount: defaultedItems.length,
      dataSourceCount: this.reportContentDataSource.data.length,
      variableColumns: this.variableColumns,
      isTabActive: this.isTabActive,
      runType: this.selectedStudyData?.runType
    });
  }

  addData(): void {
    const materialItem = this.createMaterialItem();

    this.reportContentFormArray.push(this.createReportContentFormGroup(materialItem));
    const dataTable = [...(this.reportContentDataSource.data || [])];
    dataTable.push(materialItem);
    // *MS Shallow copy updates prevent unnecessary deep cloning and keep data table mutations lightweight.*MS
    this.reportContentDataSource.data = dataTable;
    const dataTableOrder = this.buildDisplayOrderFromCurrentTable(this.reportContentDataSource.data);
    this.store.dispatch(setCalAdjustedDisplayOrder({ dataTableOrder }));
  }

  removeData(): void {
    const selectedRows = [...this.selection.selected];
    if (!selectedRows.length || !this.reportContentFormArray?.length) {
      return;
    }

    const dataTable = [...(this.reportContentDataSource.data || [])];
    const indexesToRemove = selectedRows
      .map(row => dataTable.indexOf(row))
      .filter(index => index > -1)
      .sort((a, b) => b - a);

    if (!indexesToRemove.length) {
      this.selection.clear();
      return;
    }

    indexesToRemove.forEach(index => {
      this.reportContentFormArray.removeAt(index);
      dataTable.splice(index, 1);
    });

    // *MS Updating the existing array copy keeps Angular from diffing thousands of cloned rows per removal.*MS
    this.reportContentDataSource.data = dataTable;
    const dataTableOrder = this.buildDisplayOrderFromCurrentTable(this.reportContentDataSource.data);
    this.store.dispatch(setCalAdjustedDisplayOrder({ dataTableOrder }));
    this.selection.clear();
    this.isUpdated = false;
  }

  addKitLots(): void {
    let dataTable = [...(this.reportContentDataSource.data || [])];
    let addedKitLotEntry = false;

    if (!this.isRmQcRunType()) {
      // *MS Instrument seed rows are precomputed in NgRx effects so we can inject them instantly here without
      //   regenerating the permutations every time the tab opens. That alone removed seconds from tab activation.*MS
      if (this.isCalibratorAssignmentRunType()) {
        dataTable = [];
        this.clearReportContentFormArray();
      }

      const seeds = this.preparedInstrumentSeeds;
      let addedSeed = false;
      seeds.forEach(seed => {
        const exists = dataTable.some((item) =>
          item.lot === seed.lot &&
          (item.runId || '') === seed.runId &&
          (item.assay || '') === seed.assay &&
          Number(item.region) === seed.region
        );

        if (!exists) {
          const materialItem = this.createMaterialItem(seed);
          this.reportContentFormArray.push(this.createReportContentFormGroup(materialItem));
          dataTable.push(materialItem);
          this.isUpdated = true;
          addedSeed = true;
        }
      });

      if (addedSeed) {
        // Seeded lots fall through to the kit-lot block below, so the assay count governs every run type.
        dataTable = this.applyReportContentSortingToForms(dataTable);
      }
    }

    // *MS Seed `rmqcAssayCount` placeholder rows per kit lot and instrument instead of just one, so the user
    //   can assign a different assay to each. Rows are interleaved into repeating blocks - one row per lot
    //   and instrument per block - rather than grouped by lot, so the blocks read as repeating assay sets.
    //   Bead Region is a dropdown of real panel regions, so leave it unselected (a negative sentinel never
    //   matches a real region) rather than guessing a value - a positive placeholder like 17 could
    //   coincidentally match a real region on some panels and look pre-selected while others stayed blank.
    //   The sentinel is still distinct per block so the generated reportContentItemIds stay unique.*MS
    const rowsPerGroup = Math.max(1, this.rmqcAssayCount);
    const instrumentIds = this.getAssayInstrumentIds();
    const newKitLots = this.kitLots.filter((element) => !dataTable.some((item) => item.lot === element));

    if (newKitLots.length) {
      for (let i = 0; i < rowsPerGroup; i++) {
        newKitLots.forEach((element) => {
          instrumentIds.forEach((runId) => {
            const materialItem = this.createMaterialItem({ lot: element, runId, region: -(i + 1) });
            this.reportContentFormArray.push(this.createReportContentFormGroup(materialItem));
            dataTable.push(materialItem);
          });
        });
      }
      this.isUpdated = true;
      addedKitLotEntry = true;
    }

    if (addedKitLotEntry) {
      const sortedData = this.applyReportContentSortingToForms(dataTable);
      this.reportContentDataSource.data = sortedData;
      const dataTableOrder = this.buildDisplayOrderFromCurrentTable(this.reportContentDataSource.data);
      this.store.dispatch(setCalAdjustedDisplayOrder({ dataTableOrder }));
    } else {
      this.reportContentDataSource.data = dataTable;
      const dataTableOrder = this.buildDisplayOrderFromCurrentTable(this.reportContentDataSource.data);
      this.store.dispatch(setCalAdjustedDisplayOrder({ dataTableOrder }));
    }
  }

  /** Nudges the count by one and applies it; a click on an arrow is already a deliberate change. */
  stepAssayCount(delta: number): void {
    const current = Math.trunc(Number(this.assayCountInput));
    const base = Number.isFinite(current) && current > 0 ? current : this.rmqcAssayCount;
    this.assayCountInput = Math.max(1, base + delta);
    this.applyAssayCount();
  }

  /** One assay row per kit lot and instrument is the floor, so there is nothing below it. */
  get canDecreaseAssayCount(): boolean {
    return Math.trunc(Number(this.assayCountInput)) > 1;
  }

  /**
   * Resizes the table so each kit lot and instrument pair holds exactly the entered number of assay
   * rows. Committed on change rather than on every keystroke, so typing "12" cannot briefly apply a
   * count of 1 and drop rows.
   */
  applyAssayCount(): void {
    const target = Math.trunc(Number(this.assayCountInput));
    if (!Number.isFinite(target) || target < 1) {
      // A blank or zero entry is an unfinished edit, not a request to empty the table.
      this.assayCountInput = this.rmqcAssayCount;
      this.cdr.markForCheck();
      return;
    }

    this.assayCountInput = target;
    if (!this.reportContentFormArray) {
      this.cdr.markForCheck();
      return;
    }

    const plan = this.planAssayCountResize(target);
    if (!plan.removals.length && !plan.deficits.length) {
      // Already the requested size — record the count without disturbing the table.
      this.commitAssayCount(target);
      this.cdr.markForCheck();
      return;
    }

    const filledCount = plan.removals.filter(index => !this.isPlaceholderRow(index)).length;
    if (!filledCount) {
      this.resizeAssayRows(target, plan);
      return;
    }

    this.dialog.open(CommonDialogComponent, {
      panelClass: 'bpx-dialog-container',
      disableClose: true,
      data: {
        title: 'Remove Report Items',
        message: `Setting the number of assays to ${target} removes ${plan.removals.length} ` +
          `row${plan.removals.length === 1 ? '' : 's'}, ${filledCount} of which ` +
          `${filledCount === 1 ? 'has' : 'have'} entered data. Continue?`,
        okText: 'Remove Rows',
        dismissText: 'Cancel',
        okAction: () => this.resizeAssayRows(target, plan),
        dismissAction: () => {
          this.assayCountInput = this.rmqcAssayCount;
          this.cdr.markForCheck();
        }
      }
    });
  }

  /**
   * What it would take for every kit lot and instrument pair to hold exactly `target` rows: the row
   * indexes to drop and, per pair, how many rows to add. Stating the whole move up front is what
   * makes the count a total rather than an amount to add — re-entering the same number plans nothing.
   *
   * The count only resizes lots that already have rows; it never introduces one. Seeding a newly
   * selected kit lot is `loadReportContent`'s job, and doing it here as well is what made a reloaded
   * study fan out across every lot: a loaded study's `kitLots` is rebuilt from the saved rows, so it
   * carries any lot whose rows outlived a deselection. Instruments are the opposite case — they are
   * a full dimension, so a lot the table holds gains a block for every instrument in the study, not
   * only the ones it already names.
   */
  private planAssayCountResize(target: number): AssayCountResizePlan {
    const raws = this.reportContentFormArray.getRawValue() as Record<string, unknown>[];
    const selectedKeys = this.getSelectedLotKeys();
    const indexesByGroup = new Map<string, number[]>();
    const lotLabels = new Map<string, string>();

    // The lots the table already holds are the whole scope. Grouping by the lot each row names,
    // rather than by the study's lot list, also keeps a saved lot that differs in spacing or case
    // from looking empty and being seeded a second block.
    raws.forEach((raw, index) => {
      const lotKey = this.lotKey(raw['lot']);
      if (!this.isCountedLot(lotKey, selectedKeys)) {
        return;
      }
      const key = this.assayGroupKey(raw['lot'], raw['runId']);
      if (!indexesByGroup.has(key)) {
        indexesByGroup.set(key, []);
        lotLabels.set(key, String(raw['lot'] ?? ''));
      }
      indexesByGroup.get(key)?.push(index);
    });

    // Every counted lot owes a block per instrument, so pairs the table has no row for yet are
    // planned as a full deficit rather than going unnoticed.
    const instrumentIds = this.getAssayInstrumentIds();
    const lotsInScope = new Map<string, string>();
    raws.forEach(raw => {
      const lotKey = this.lotKey(raw['lot']);
      if (this.isCountedLot(lotKey, selectedKeys) && !lotsInScope.has(lotKey)) {
        lotsInScope.set(lotKey, String(raw['lot'] ?? ''));
      }
    });

    const removals: number[] = [];
    const deficits: AssayCountDeficit[] = [];
    lotsInScope.forEach((lotLabel, lotKey) => {
      instrumentIds.forEach(runId => {
        const key = this.assayGroupKey(lotKey, runId);
        const indexes = indexesByGroup.get(key) ?? [];
        if (indexes.length > target) {
          // The trailing rows of a pair are its trailing blocks.
          removals.push(...indexes.slice(target));
          return;
        }
        if (indexes.length < target) {
          deficits.push({ lot: lotLabel, runId, key, count: target - indexes.length });
        }
      });
    });

    // Rows naming an instrument the study no longer lists are still the count's to trim.
    indexesByGroup.forEach((indexes, key) => {
      const knownPair = deficits.some(deficit => deficit.key === key) ||
        instrumentIds.some(runId => this.assayGroupKey(lotLabels.get(key), runId) === key);
      if (!knownPair && indexes.length > target) {
        removals.push(...indexes.slice(target));
      }
    });

    return { removals: [...new Set(removals)].sort((a, b) => a - b), deficits };
  }

  /** Kit lots reach the table from several sources, so they are only ever compared normalized. */
  private lotKey(value: unknown): string {
    return String(value ?? '').trim().toUpperCase();
  }

  /**
   * The count is a total per kit lot *and* instrument, so a lot measured on two instruments holds
   * two blocks of rows. Pairing them in one key keeps every lookup on the same grain.
   */
  private assayGroupKey(lot: unknown, runId: unknown): string {
    return `${this.lotKey(lot)}|${this.lotKey(runId)}`;
  }

  /**
   * The instruments the count fans out across. A study with no instrument files still has one
   * block per kit lot, carrying a blank instrument, which is what the table held before
   * instruments became a dimension.
   */
  private getAssayInstrumentIds(): string[] {
    const unique = Array.from(new Set(
      (this.runIds ?? []).map(id => (id ?? '').trim()).filter(id => !!id)
    ));
    return unique.length ? unique : [''];
  }

  /** The kit lots the user chose in the Select Kit Lots dialog, normalized for comparison. */
  private getSelectedLotKeys(): Set<string> {
    return new Set((this.kitLots ?? []).map(lot => this.lotKey(lot)).filter(key => !!key));
  }

  /**
   * Whether the assay count governs a row's kit lot. Rows can outlive a narrowed selection — the
   * operator may keep them when deselecting a lot — and those kept rows are not the count's to
   * resize. With no selection recorded, the lots already in the table are the only scope there is.
   */
  private isCountedLot(key: string, selectedKeys: Set<string>): boolean {
    return !!key && (!selectedKeys.size || selectedKeys.has(key));
  }

  /**
   * Points the count at what the table actually holds. A saved study need not carry the count —
   * older studies predate the field — and showing a stale 1 over a table of three rows per lot
   * both misreports the table and makes the next edit look as though it added rows instead of
   * setting a total.
   */
  private syncAssayCountToTable(): void {
    const rowsPerGroup = this.getAssayRowsPerGroup();
    if (rowsPerGroup > 0) {
      this.rmqcAssayCount = rowsPerGroup;
    }
    this.assayCountInput = this.rmqcAssayCount;
  }

  /**
   * Rows the table holds per selected kit lot and instrument — the most any one pair has, ignoring
   * dropped lots. Counting per pair rather than per lot is what keeps the box reading 3 for a lot
   * measured three times on each of two instruments, instead of 6.
   */
  private getAssayRowsPerGroup(): number {
    const selectedKeys = this.getSelectedLotKeys();
    const counts = new Map<string, number>();
    (this.reportContentDataSource.data ?? []).forEach(item => {
      if (this.isCountedLot(this.lotKey(item.lot), selectedKeys)) {
        const key = this.assayGroupKey(item.lot, item.runId);
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    });
    return Math.max(0, ...counts.values());
  }

  /** A seeded row nobody has typed into yet: no assay, no real bead region, no material entries. */
  private isPlaceholderRow(index: number): boolean {
    const group = this.reportContentFormArray.at(index) as FormGroup | undefined;
    if (!group) {
      return true;
    }

    const raw = group.getRawValue() as Record<string, unknown>;
    if (this.isRealBeadRegion(raw['region'])) {
      return false;
    }
    return !hasOperatorEnteredData(raw, this.variableColumns);
  }

  private resizeAssayRows(target: number, plan: AssayCountResizePlan): void {
    const dataTable = [...(this.reportContentDataSource.data || [])];

    // Descending, so each splice cannot shift the indexes still to be removed.
    [...plan.removals].sort((a, b) => b - a).forEach(index => {
      this.reportContentFormArray.removeAt(index);
      dataTable.splice(index, 1);
    });
    this.appendAssayRowBlocks(plan.deficits, dataTable);

    this.commitAssayCount(target);
    this.reportContentDataSource.data = dataTable;
    this.publishDisplayOrder(dataTable);
    this.selection.clear();
    this.isUpdated = false;
    this.cdr.markForCheck();

    console.info('[RunReportItems] resizeAssayRows', {
      target,
      removed: plan.removals.length,
      added: plan.deficits.reduce((total, deficit) => total + deficit.count, 0),
      rowCount: dataTable.length
    });
  }

  /** Stores the count on the study, so it survives a tab switch and seeds any kit lot added later. */
  private commitAssayCount(target: number): void {
    const changed = this.rmqcAssayCount !== target;
    this.rmqcAssayCount = target;
    this.assayCountInput = target;
    if (changed) {
      // Re-entering the count the study already holds is not a change to save.
      this.store.dispatch(setRmqcAssayCount({ rmqcAssayCount: target }));
    }
  }

  /** Tells the cal-adjusted views which order the table now reads in. */
  private publishDisplayOrder(rows: ReportContentItem[]): void {
    this.store.dispatch(setCalAdjustedDisplayOrder({
      dataTableOrder: this.buildDisplayOrderFromCurrentTable(rows)
    }));
  }

  /**
   * Adds each shortfall one row per lot-and-instrument pair per block, so the blocks read as assay
   * sets across every pair rather than listing one pair's rows before the next pair's.
   */
  private appendAssayRowBlocks(deficits: AssayCountDeficit[], dataTable: ReportContentItem[]): void {
    if (!deficits.length) {
      return;
    }

    // Sentinels are tracked per pair, so two instruments on the same lot can both start at -1;
    // their rows still get distinct ids because the id carries the instrument.
    const usedRegions = new Map<string, Set<number>>();
    deficits.forEach(deficit => usedRegions.set(deficit.key, new Set<number>()));
    dataTable.forEach(item => {
      usedRegions.get(this.assayGroupKey(item.lot, item.runId))?.add(this.parseRegion(item.region));
    });

    const deepestDeficit = Math.max(...deficits.map(deficit => deficit.count));
    for (let block = 0; block < deepestDeficit; block++) {
      deficits.forEach(deficit => {
        if (block >= deficit.count) {
          return;
        }
        const materialItem = this.createMaterialItem({
          lot: deficit.lot,
          runId: deficit.runId,
          region: this.nextRegionSentinel(usedRegions.get(deficit.key))
        });
        this.reportContentFormArray.push(this.createReportContentFormGroup(materialItem));
        dataTable.push(materialItem);
      });
    }
  }

  /**
   * A negative bead region, so no real panel region matches it and the dropdown reads as
   * unselected. Skipping the sentinels a lot already carries keeps the generated
   * reportContentItemIds unique when a middle row was removed.
   */
  private nextRegionSentinel(used?: Set<number>): number {
    let candidate = 1;
    while (used?.has(-candidate)) {
      candidate += 1;
    }
    used?.add(-candidate);
    return -candidate;
  }

  isRowSelected(rowIndex: number): boolean {
    const row = this.reportContentDataSource.data[rowIndex];
    return !!row && this.selection.isSelected(row);
  }

  toggleRow(rowIndex: number): void {
    const row = this.reportContentDataSource.data[rowIndex];
    if (!row) {
      return;
    }
    this.selection.toggle(row);
  }

  isAllSelected(): boolean {
    const numRows = this.reportContentDataSource.data.length;
    const numSelected = this.selection.selected.length;
    return numRows > 0 && numSelected === numRows;
  }

  toggleAllRows(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this.reportContentDataSource.data);
  }

  checkboxLabel(rowIndex?: number): string {
    if (rowIndex === undefined) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all rows`;
    }
    return `${this.isRowSelected(rowIndex) ? 'deselect' : 'select'} row ${rowIndex + 1}`;
  }

  onAssayChange(event: MatSelectChange, index: number): void {
    const selectedAssayName = event.value;
    const reportContentArray = this.reportContentFormGroup.get('reportContent') as FormArray;
    const rowGroup = reportContentArray.at(index) as FormGroup;
    this.syncRegionWithAssaySelection(rowGroup, selectedAssayName);
  }

  autofillFunc(): void {
    const reportContentData = this.reportContentFormGroup.value.reportContent;
    if (reportContentData && reportContentData.length > 0) {
      const firstObject = reportContentData[0];
      for (let i = 1; i < reportContentData.length; i++) {
        const currentObject = reportContentData[i];
        Object.keys(firstObject).forEach((key) => {
          if (key !== 'lot' && key !== 'assay' && key !== 'resultType' && key !== 'region') {
            currentObject[key] = firstObject[key];
          }
        });
      }
      this.reportContentFormGroup.setValue({
        reportContent: reportContentData
      });
    }
  }

  openExcelSelector(): void {
    this.excelSelector?.nativeElement.click();
  }

  async handleExcelSelection(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    // Cleared so picking the same file twice still raises a change event.
    input.value = '';
    if (!file) {
      return;
    }
    await this.importExcelFile(file);
  }

  /** Reads an uploaded workbook, previews what it would do, and applies it if confirmed. */
  async importExcelFile(file: File): Promise<void> {
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      this.appUtilityService.showSnackbar('Only .xlsx workbooks can be imported.');
      return;
    }

    this.isImporting = true;
    this.cdr.markForCheck();
    try {
      const parsed = await this.excelService.parseFile(file, this.buildImportContext());
      const plan = this.planImport(parsed.rows);
      const data: ReportItemsImportDialogData = {
        fileName: parsed.fileName,
        sheetName: parsed.sheetName,
        updateCount: plan.filter(entry => entry.targetIndex !== null).length,
        addCount: plan.filter(entry => entry.targetIndex === null).length,
        incompleteCount: parsed.incompleteCount,
        skippedRows: parsed.skipped.map(skip => ({ sheetRow: skip.sheetRow, reason: skip.reason })),
        ignoredHeaders: parsed.ignoredHeaders
      };

      const confirmed = await firstValueFrom(
        this.dialog.open<ReportItemsImportDialogComponent, ReportItemsImportDialogData, boolean>(
          ReportItemsImportDialogComponent,
          { autoFocus: false, panelClass: 'bpx-dialog-container', data }
        ).afterClosed()
      );

      if (confirmed) {
        this.applyImportPlan(plan);
      }
    } catch (error) {
      console.error('[RunReportItems] importExcelFile', error);
      this.appUtilityService.showSnackbar(
        error instanceof Error ? error.message : 'The workbook could not be read.'
      );
    } finally {
      this.isImporting = false;
      this.cdr.markForCheck();
    }
  }

  /** Exports the table as the workbook the importer expects back, headers and current rows alike. */
  async downloadExcelTemplate(): Promise<void> {
    try {
      const columns = this.buildTemplateColumns();
      const rows = (this.reportContentFormArray?.getRawValue() ?? []).map(raw =>
        columns.map(column => this.buildTemplateCell(raw as Record<string, unknown>, column.formKey))
      );
      await this.excelService.downloadTemplate({
        fileName: this.buildTemplateFileName(),
        headers: columns.map(column => column.label),
        rows
      });
    } catch (error) {
      console.error('[RunReportItems] downloadExcelTemplate', error);
      this.appUtilityService.showSnackbar('The template could not be generated.');
    }
  }

  private buildImportContext(): ReportItemsImportContext {
    return {
      kitLots: this.kitLots ?? [],
      instrumentIds: (this.runIds ?? []).filter(runId => !!runId),
      assayNames: this.assayNames ?? [],
      beadRegions: this.beadRegions ?? [],
      variableColumns: this.variableColumns ?? [],
      variableColumnLabels: Object.fromEntries(
        this.variableColumnMeta.map(meta => [meta.id, meta.label])
      ),
      forcedResultType: this.getDefaultResultTypeForRunType()
    };
  }

  /**
   * Decides, for each uploaded row, which table row it lands on. An exact kit lot / instrument /
   * assay match is overwritten; failing that, a row seeded for the same kit lot but with no assay
   * chosen yet is filled in, so an upload completes the placeholder rows this tab seeds rather than
   * appending duplicates beside them. Anything left over becomes a new row.
   */
  private planImport(rows: ReportItemsImportRow[]): ReportItemsImportPlanEntry[] {
    const current = (this.reportContentFormArray?.getRawValue() ?? []) as Record<string, unknown>[];
    const claimed = new Set<number>();

    const findExactMatch = (row: ReportItemsImportRow): number => current.findIndex((raw, index) =>
      !claimed.has(index) &&
      String(raw['lot'] ?? '') === row.lot &&
      String(raw['runId'] ?? '') === row.runId &&
      // Strict canonical equality, not the app's looser substring match: "SSA" must not claim the
      // "SSA52" row.
      this.getCanonicalAssayName(String(raw['assay'] ?? '')) === this.getCanonicalAssayName(row.assay)
    );

    const findPlaceholder = (row: ReportItemsImportRow): number => current.findIndex((raw, index) => {
      if (claimed.has(index) || String(raw['lot'] ?? '') !== row.lot) {
        return false;
      }
      if (String(raw['assay'] ?? '').trim()) {
        return false;
      }
      const runId = String(raw['runId'] ?? '').trim();
      return !runId || runId === row.runId;
    });

    return rows.map(row => {
      let targetIndex = findExactMatch(row);
      if (targetIndex < 0) {
        targetIndex = findPlaceholder(row);
      }
      if (targetIndex >= 0) {
        claimed.add(targetIndex);
      }
      return { row, targetIndex: targetIndex >= 0 ? targetIndex : null };
    });
  }

  private applyImportPlan(plan: ReportItemsImportPlanEntry[]): void {
    if (!plan.length || !this.reportContentFormArray) {
      return;
    }

    let addedRows = 0;
    plan.forEach(entry => {
      if (entry.targetIndex === null) {
        const materialItem = this.createMaterialItem({
          lot: entry.row.lot,
          runId: entry.row.runId,
          assay: entry.row.assay
        });
        const formGroup = this.createReportContentFormGroup(materialItem);
        this.reportContentFormArray.push(formGroup);
        this.patchImportedRow(formGroup, entry.row);
        addedRows += 1;
        return;
      }
      this.patchImportedRow(this.reportContentFormArray.at(entry.targetIndex) as FormGroup, entry.row);
    });

    // Rebuild the rendered rows from the form's values so sorting and the cal-adjusted display
    // order see the imported lots and regions rather than the pre-import snapshot.
    const dataTable = this.reportContentFormArray.getRawValue()
      .map(raw => this.buildReportContentItemFromRaw(raw as Record<string, unknown>));
    const orderedData = addedRows ? this.applyReportContentSortingToForms(dataTable) : dataTable;

    this.reportContentDataSource.data = orderedData;
    const dataTableOrder = this.buildDisplayOrderFromCurrentTable(orderedData);
    this.store.dispatch(setCalAdjustedDisplayOrder({ dataTableOrder }));
    this.selection.clear();
    this.isUpdated = false;
    this.cdr.markForCheck();

    console.info('[RunReportItems] applyImportPlan', {
      updated: plan.length - addedRows,
      added: addedRows,
      rowCount: orderedData.length
    });
  }

  private patchImportedRow(formGroup: FormGroup, row: ReportItemsImportRow): void {
    const { region, ...values } = row.values;
    // Assay goes in first: choosing one syncs the bead region, so a region from the sheet can only
    // stick if it is written afterwards.
    formGroup.patchValue(values);
    if (region !== undefined) {
      formGroup.get('region')?.setValue(region, { emitEvent: false });
    }
  }

  private buildTemplateColumns(): { label: string; formKey: string }[] {
    return [
      ...REPORT_ITEM_COLUMNS.map(column => ({ label: column.label, formKey: column.formKey })),
      ...this.variableColumnMeta.map(meta => ({ label: meta.label, formKey: meta.id }))
    ];
  }

  private buildTemplateCell(raw: Record<string, unknown>, formKey: string): string | number | Date | null {
    const value = raw[formKey];
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (formKey === 'region' && !this.isRealBeadRegion(value)) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    if (typeof value === 'number') {
      return value;
    }
    return String(value);
  }

  /**
   * A seeded row whose assay has not been chosen carries a negative placeholder region, which the
   * dropdown renders as unselected. It is a sentinel, not a value, so it stays out of exports.
   */
  private isRealBeadRegion(region: unknown): boolean {
    const parsed = Number(region);
    return Number.isFinite(parsed) && parsed > 0;
  }

  private buildTemplateFileName(): string {
    const studySlug = (this.studyName || 'study').trim().replace(/[^A-Za-z0-9._-]+/g, '-');
    return `${studySlug}-report-items.xlsx`;
  }

  navigateToPrintView(): void {
    this.router.navigateByUrl('/(print:print/preview-run-report-items)');
  }

  /** Columns chosen in the print preview bar; held so a rebuild cannot silently widen the print. */
  handlePrintColumnSelection(selectedColumns: ColumnSelection[]): void {
    this.printColumnSelection = selectedColumns;
    this.handleSelectColumns(selectedColumns);
  }

  /** Identifies the document in the print preview bar, beside the study name. */
  get printContextLine(): string {
    return [
      this.selectedStudyData?.runType,
      this.selectedStudyData?.testSpec ? `Test Spec ${this.selectedStudyData.testSpec}` : '',
      this.specVersion ? `Spec Rev ${this.specVersion}` : ''
    ].filter(Boolean).join(' · ');
  }

  handleSelectColumns(selectedColumns: ColumnSelection[]): void {
    this.selectedPrintColumns = selectedColumns;
    if (this.isPrintMode) {
      this.printPagesSnapshot = this.buildPrintPages();
    }
  }

  get printPages(): ColumnSelection[][] {
    return this.printPagesSnapshot;
  }

  storeFormValues(): void {
    if (this.isEmpty) {
      return;
    }

    this.syncFormGroupIdsFromValues();

    const changedItems = this.getChangedReportContentItems();
    const variableColumnsChanged = this.haveVariableColumnsChanged();
    const removedItemIds = this.getRemovedReportContentItemIds();

    if (!changedItems.length && !variableColumnsChanged && !removedItemIds.length) {
      // *MS Skip dispatching when neither the grid rows nor the variable column layout changed; this keeps NgRx quiet during no-op saves.*MS
      this.isUpdated = true;
      return;
    }

    const payload = {
      changedItems,
      ...(variableColumnsChanged ? { variableColumns: this.variableColumns } : {}),
      ...(removedItemIds.length ? { removedItemIds } : {})
    };

    // Row ids are derived from the row values, so edited rows come back from the store under new ids and
    // the merge appends them. Record the on-screen order by the new ids before the store echoes back.
    this.pendingDisplayOrder = (this.reportContentFormArray?.getRawValue() ?? []).map(
      (raw: Record<string, unknown>) => this.buildReportContentItemFromRaw(raw).reportContentItemId
    );
    this.store.dispatch(patchReportContentItems(payload));
    this.updateBaselineForChanges(changedItems);
    if (removedItemIds.length) {
      this.removeReportContentBaselines(removedItemIds);
    }
    if (variableColumnsChanged) {
      this.refreshVariableColumnSignature();
    }
    this.refreshFormReportContentIds();
    this.isUpdated = true;
  }

  // *MS Compare current form values against the cached signatures so we only send the rows that actually changed.*MS
  private getChangedReportContentItems(): ReportContentItem[] {
    const rawItems = this.reportContentFormArray?.getRawValue() ?? [];
    const changes: ReportContentItem[] = [];
    rawItems.forEach((raw: Record<string, unknown>) => {
      const item = this.buildReportContentItemFromRaw(raw);
      const signature = this.computeRowSignature(item);
      const baseline = this.reportContentBaselines.get(item.reportContentItemId);
      if (!baseline || baseline !== signature) {
        changes.push(item);
      }
    });
    return changes;
  }

  private getRemovedReportContentItemIds(): string[] {    // Baselines that no longer have a matching FormArray row signify a real removal.
    const activeIds = this.getActiveFormReportContentIds();
    const removed: string[] = [];
    this.reportContentBaselines.forEach((_signature, id) => {
      if (!activeIds.has(id)) {
        removed.push(id);
      }
    });
    return removed;
  }

  private getActiveFormReportContentIds(): Set<string> {
    const ids = new Set<string>();
    this.reportContentFormArray?.controls.forEach((group: FormGroup) => {
      const id = group.get('reportContentItemId')?.value;
      if (id) {
        ids.add(id);
      }
    });
    return ids;
  }

  // *MS Normalize each FormArray row into the same shape NgRx stores so signature comparisons stay consistent.*MS
  private buildReportContentItemFromRaw(raw: Record<string, unknown>): ReportContentItem {
    const materialItem: ReportContentItem = {
      reportContentItemId: '',
      lot: (raw['lot'] as string) ?? '',
      runId: String(raw['runId'] ?? ''),
      assay: (raw['assay'] as string) ?? '',
      resultType: (raw['resultType'] as string) ?? '',
      region: Number(raw['region'] ?? 0),
      beadCouplingDate: this.formatCouplingDate(raw['beadCouplingDate']),
      coupledBeadLn: (raw['coupledBeadLn'] as string) ?? '',
      dyedBeadLn: (raw['dyedBeadLn'] as string) ?? '',
      progenitorLn: (raw['progenitorLn'] as string) ?? '',
      antigenLn: (raw['antigenLn'] as string) ?? '',
      antigenLoadLn: (raw['antigenLoadLn'] as string) ?? '',
      coupledBeadPn: (raw['coupledBeadPn'] as string) ?? '',
      ligandPn: (raw['ligandPn'] as string) ?? '',
      beadReagentPn: (raw['beadReagentPn'] as string) ?? '',
      beadReagentLn: (raw['beadReagentLn'] as string) ?? '',
      conjugateReagentLn: (raw['conjugateReagentLn'] as string) ?? '',
      particleDiluentLn: (raw['particleDiluentLn'] as string) ?? '',
      conjugateDiluentLn: (raw['conjugateDiluentLn'] as string) ?? '',
      washBufferLn: (raw['washBufferLn'] as string) ?? '',
      sheathFluidLn: (raw['sheathFluidLn'] as string) ?? '',
      sampleDiluentLn: (raw['sampleDiluentLn'] as string) ?? '',
      description: (raw['description'] as string) ?? '',
      units: (raw['units'] as string) ?? '',
      resultDecimalPrecision: Number(raw['resultDecimalPrecision'] ?? 0),
      userDefinedDecimalPrecision: Number(raw['userDefinedDecimalPrecision'] ?? 0),
      calLot: (raw['calLot'] as string) ?? '',
      qcLot: (raw['qcLot'] as string) ?? ''
    };

    this.variableColumns.forEach((column: string) => {
      materialItem[column] = (raw[column] as string) ?? '';
    });
    materialItem.reportContentItemId = this.buildReportContentItemId(materialItem);
    return materialItem;
  }

  private buildReportContentItemId(item: Partial<ReportContentItem>): string {
    const lot = item.lot ?? '';
    const region = this.parseRegion(item.region);
    const runId = item.runId ?? '';
    return `${lot}${region}_${runId}`;
  }

  private createReportContentFormGroup(materialItem: ReportContentItem): FormGroup {
    const formGroupData = new FormGroup({
      reportContentItemId: new FormControl(materialItem.reportContentItemId),
      lot: new FormControl(materialItem.lot),
      runId: new FormControl(materialItem.runId),
      assay: new FormControl(materialItem.assay),
      resultType: new FormControl(materialItem.resultType),
      region: new FormControl(materialItem.region),
      beadCouplingDate: new FormControl(materialItem.beadCouplingDate ? new Date(materialItem.beadCouplingDate) : null),
      coupledBeadLn: new FormControl(materialItem.coupledBeadLn),
      dyedBeadLn: new FormControl(materialItem.dyedBeadLn),
      progenitorLn: new FormControl(materialItem.progenitorLn),
      antigenLn: new FormControl(materialItem.antigenLn),
      antigenLoadLn: new FormControl(materialItem.antigenLoadLn),
      coupledBeadPn: new FormControl(materialItem.coupledBeadPn),
      ligandPn: new FormControl(materialItem.ligandPn),
      beadReagentPn: new FormControl(materialItem.beadReagentPn),
      beadReagentLn: new FormControl(materialItem.beadReagentLn),
      conjugateReagentLn: new FormControl(materialItem.conjugateReagentLn),
      particleDiluentLn: new FormControl(materialItem.particleDiluentLn),
      conjugateDiluentLn: new FormControl(materialItem.conjugateDiluentLn),
      washBufferLn: new FormControl(materialItem.washBufferLn),
      sheathFluidLn: new FormControl(materialItem.sheathFluidLn),
      sampleDiluentLn: new FormControl(materialItem.sampleDiluentLn),
      description: new FormControl(materialItem.description),
      units: new FormControl(materialItem.units),
      resultDecimalPrecision: new FormControl(materialItem.resultDecimalPrecision),
      userDefinedDecimalPrecision: new FormControl(materialItem.userDefinedDecimalPrecision),
      calLot: new FormControl(materialItem.calLot),
      qcLot: new FormControl(materialItem.qcLot)
    } as { [key: string]: FormControl });

    this.variableColumns?.forEach((field) => {
      formGroupData.addControl(field, new FormControl(materialItem[field]));
    });

    this.registerAssayRegionSync(formGroupData);
    return formGroupData;
  }

  private registerAssayRegionSync(formGroup: FormGroup): void {
    const assayControl = formGroup.get('assay');
    if (!assayControl) {
      return;
    }

    assayControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((assayName) => this.syncRegionWithAssaySelection(formGroup, assayName));
  }

  private syncRegionWithAssaySelection(formGroup: FormGroup, assayName: unknown): void {
    const normalizedAssay = typeof assayName === 'string' ? assayName.trim() : '';
    if (!normalizedAssay) {
      return;
    }

    const resolvedRegion = this.resolveAssayRegion(normalizedAssay);
    if (resolvedRegion === null) {
      return;
    }

    const regionControl = formGroup.get('region');
    if (!regionControl) {
      return;
    }

    if (this.parseRegion(regionControl.value as string | number | undefined) === resolvedRegion) {
      return;
    }

    regionControl.setValue(resolvedRegion, { emitEvent: false });
  }

  private createMaterialItem(seed?: Partial<ReportContentSeed>): ReportContentItem {
    const defaultResultType = this.getDefaultResultTypeForRunType();
    const materialItem: ReportContentItem = {
      lot: seed?.lot ?? '', runId: seed?.runId ?? this.getRmQcDefaultInstrumentId(), assay: seed?.assay ?? '', region: seed?.region ?? 17,
      resultType: defaultResultType ?? (seed?.resultType ?? (this.isRmQcRunType() ? 'Calculated' : 'Instrument')), beadCouplingDate: '',
      coupledBeadLn: '', dyedBeadLn: '', progenitorLn: '', antigenLn: '', antigenLoadLn: '',
      coupledBeadPn: '', ligandPn: '', beadReagentPn: '', description: '', units: '',
      resultDecimalPrecision: 2, userDefinedDecimalPrecision: 2, calLot: '', qcLot: '', reportContentItemId: '',
      beadReagentLn: '', conjugateReagentLn: '', particleDiluentLn: '', conjugateDiluentLn: '', washBufferLn: '',
      sheathFluidLn: '', sampleDiluentLn: ''
    };

    this.variableColumns?.forEach((column: string) => {
      materialItem[column] = materialItem[column] ?? '';
    });

    materialItem.reportContentItemId = this.buildReportContentItemId(materialItem);
    return materialItem;
  }

  private getRmQcDefaultInstrumentId(): string {
    if (!this.isRmQcRunType()) {
      return '';
    }

    const uniqueInstrumentIds = Array.from(new Set((this.runIds ?? []).filter(id => !!id && id.trim().length)));
    if (uniqueInstrumentIds.length !== 1) {
      return '';
    }

    return uniqueInstrumentIds[0];
  }

  private captureReportContentBaselines(items: ReportContentItem[]): void {
    this.reportContentBaselines.clear();
    items.forEach(item => {
      const canonical: ReportContentItem = {
        ...item,
        reportContentItemId: this.buildReportContentItemId(item)
      };
      // *MS Store a deterministic signature for each row so later saves only serialize rows whose values changed.*MS
      this.reportContentBaselines.set(canonical.reportContentItemId, this.computeRowSignature(canonical));
    });
  }

  // *MS Persist the new signatures immediately after dispatch so a second quick save sees a clean baseline.*MS
  private updateBaselineForChanges(items: ReportContentItem[]): void {
    items.forEach(item => {
      this.reportContentBaselines.set(item.reportContentItemId, this.computeRowSignature(item));
    });
  }

  private removeReportContentBaselines(ids: string[]): void { ids.forEach(id => this.reportContentBaselines.delete(id));  }

  private syncFormGroupIdsFromValues(): void { this.reportContentFormArray?.controls.forEach((group: FormGroup) => {
      const raw = group.getRawValue();
      const item = this.buildReportContentItemFromRaw(raw as Record<string, unknown>);
      const nextId = item.reportContentItemId;
      const control = group.get('reportContentItemId');
      if (control && control.value !== nextId) {
        control.setValue(nextId, { emitEvent: false });
      }
    });
  }

  private refreshFormReportContentIds(): void {this.reportContentFormArray?.controls.forEach((group: FormGroup) => {
      const previousId = group.get('reportContentItemId')?.value;
      const raws = group.getRawValue();
      const item = this.buildReportContentItemFromRaw(raws as Record<string, unknown>);
      const nextId = item.reportContentItemId;
      if (previousId && previousId !== nextId) {
        this.reportContentBaselines.delete(previousId);
      }
      group.get('reportContentItemId')?.setValue(nextId, { emitEvent: false });
    });
  }

  private computeRowSignature(item: ReportContentItem): string {
    const keys = Object.keys(item).sort();
    return keys.map(key => `${key}:${(item as Record<string, unknown>)[key] ?? ''}`).join('|');
  }

  private haveVariableColumnsChanged(): boolean {
    return this.computeVariableColumnSignature(this.variableColumns) !== this.variableColumnsSignature;
  }

  private refreshVariableColumnSignature(): void {
    this.variableColumnsSignature = this.computeVariableColumnSignature(this.variableColumns);
  }

  private computeVariableColumnSignature(columns: string[]): string {
    return (columns ?? []).join('|');
  }

  private formatCouplingDate(value: unknown): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value as string);
    if (Number.isNaN(date.valueOf())) {
      return '';
    }
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  }

  private applyReportContentSortingToForms(dataTable: ReportContentItem[]): ReportContentItem[] {
    if (this.isRmQcRunType() || !this.reportContentFormArray?.length) {
      return dataTable;
    }

    const paired = dataTable.map((item, index) => ({
      item,
      control: this.reportContentFormArray.at(index) as FormGroup
    }));

    paired.sort((a, b) => this.compareReportContentItems(a.item, b.item));

    this.resetReportContentFormControls(paired.map(pair => pair.control));
    return paired.map(pair => pair.item);
  }

  private preserveDisplayedOrder(items: ReportContentItem[]): ReportContentItem[] {
    const currentIds = this.pendingDisplayOrder
      ?? (this.reportContentDataSource?.data ?? []).map(row => row.reportContentItemId);
    if (!currentIds.length || !items.length) {
      return items;
    }

    const position = new Map<string, number>();
    currentIds.forEach((id, index) => {
      if (id && !position.has(id)) {
        position.set(id, index);
      }
    });

    const known: ReportContentItem[] = [];
    const unseen: ReportContentItem[] = [];
    items.forEach(item => (position.has(item.reportContentItemId) ? known : unseen).push(item));
    if (!known.length) {
      return items;
    }

    known.sort((a, b) => position.get(a.reportContentItemId)! - position.get(b.reportContentItemId)!);
    return [...known, ...unseen];
  }

  private sortReportContentItems(items: ReportContentItem[]): ReportContentItem[] {
    const list = [...(items ?? [])];
    if (this.isRmQcRunType()) {
      return list;
    }

    return list.sort((a, b) => this.compareReportContentItems(a, b));
  }

  private compareReportContentItems(a: ReportContentItem, b: ReportContentItem): number {
    // Seeded rows carry a negative sentinel region, so sorting by region alone would lift every
    // unfilled row above the real ones. They trail instead, keeping their interleaved block order.
    const aSeeded = isSeededPlaceholderRegion(a.region);
    const bSeeded = isSeededPlaceholderRegion(b.region);
    if (aSeeded || bSeeded) {
      return aSeeded === bSeeded ? 0 : (aSeeded ? 1 : -1);
    }

    const aInternal = this.isInternalQcBeadRegion(a.region);
    const bInternal = this.isInternalQcBeadRegion(b.region);

    if (aInternal !== bInternal) {
      return aInternal ? 1 : -1;
    }

    const regionDiff = this.parseRegion(a.region) - this.parseRegion(b.region);
    if (regionDiff !== 0) {
      return regionDiff;
    }

    return (a.assay ?? '').localeCompare(b.assay ?? '');
  }

  private isInternalQcBeadRegion(region: number | string | undefined): boolean {
    return isInternalQcBeadRegion(region);
  }
  private parseRegion(region: string | number | undefined): number {
    if (typeof region === 'number') {
      return Number.isNaN(region) ? 0 : region;
    }

    const parsed = Number(region);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private resetReportContentFormControls(controls: FormGroup[]): void {
    if (!this.reportContentFormArray) {
      return;
    }

    while (this.reportContentFormArray.length) {
      this.reportContentFormArray.removeAt(this.reportContentFormArray.length - 1);
    }

    controls.forEach(control => this.reportContentFormArray.push(control));
  }

  private clearReportContentFormArray(): void {
    if (!this.reportContentFormArray) {
      return;
    }

    while (this.reportContentFormArray.length) {
      this.reportContentFormArray.removeAt(this.reportContentFormArray.length - 1);
    }
    this.selection.clear();
  }

  private isCalibratorAssignmentRunType(): boolean {
    return (this.selectedStudyData?.runType ?? '').toLowerCase() === 'calibrator assignment';
  }

  private isRmQcRunType(): boolean {
    return (this.selectedStudyData?.runType ?? '').toUpperCase() === 'RMQC';
  }

  private getDefaultResultTypeForRunType(): 'Calculated' | 'Instrument' | null {
    const raw = (this.selectedStudyData?.runType ?? '').trim();
    if (!raw) {
      return null;
    }
    const normalized = raw.toLowerCase().replace(/\s+/g, ' ');
    const calculatedTypes = new Set([
      'rmqc',
      'prebulk',
      'calibratorassignment',
      'calibrator assignment',
      'calibrator adjustment'
    ]);
    const instrumentTypes = new Set([
      'matchkit',
      'stability',
      'control assignment'
    ]);
    if (calculatedTypes.has(normalized)) {
      return 'Calculated';
    }
    if (instrumentTypes.has(normalized)) {
      return 'Instrument';
    }
    return null;
  }

  private assayNamesMatch(nameA: string | null | undefined, nameB: string | null | undefined): boolean {
    const canonicalA = this.getCanonicalAssayName(nameA);
    const canonicalB = this.getCanonicalAssayName(nameB);
    if (!canonicalA || !canonicalB) {
      return false;
    }
    return canonicalA === canonicalB
      || canonicalA.includes(canonicalB)
      || canonicalB.includes(canonicalA);
  }

  private getCanonicalAssayName(name: string | null | undefined): string {
    if (!name) {
      return '';
    }
    return name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  }

  private resolveAssayRegion(assayName: string): number | null {
    if (!assayName) {
      return null;
    }

    const targetKey = this.getCanonicalAssayName(assayName);
    const matchedExactAssay = this.assayBeadMapping?.assays?.find(assay => {
      const rawName = (assay?.name ?? '').trim();
      if (!rawName || rawName.toLowerCase() === 'nan') {
        return false;
      }
      return this.getCanonicalAssayName(rawName) === targetKey;
    });

    const matchedAssay = matchedExactAssay ?? this.assayBeadMapping?.assays?.find(assay => {
      const rawName = (assay?.name ?? '').trim();
      if (!rawName || rawName.toLowerCase() === 'nan') {
        return false;
      }
      return this.assayNamesMatch(rawName, assayName);
    });

    if (matchedAssay) {
      const region = Number(matchedAssay.region ?? 0);
      return Number.isFinite(region) && region > 0 ? region : null;
    }

    const lookupKey = this.getCanonicalAssayName(assayName);
    const fallback = this.assayRegionLookup.get(lookupKey);
    return Number.isFinite(fallback ?? NaN) ? fallback! : null;
  }

  private findPanelAssayMapping(study: Study): PatientFilePanelInfo | null {
    const keys = this.getPanelMatchKeys(study);
    if (!keys.length) {
      return null;
    }

    const metadataSources = [
      ...(study.patientFileMetadata ?? []),
      ...(study.calFileMetadata ?? []),
      ...(study.controlFileMetadata ?? [])
    ];

    for (const file of metadataSources) {
      const panels = file?.panels ?? [];
      for (const panel of panels) {
        const normalized = this.normalizePanelAlias(panel?.name);
        if (normalized && keys.includes(normalized)) {
          return panel;
        }
      }
    }

    return null;
  }

  private buildAssayRegionLookup(study: Study): Map<string, number> {
    const lookup = new Map<string, number>();
    (study.panelSpecs?.Assay_Specs ?? []).forEach(spec => {
      const key = this.getCanonicalAssayName(spec.assay_name);
      const region = Number(spec.bead_region);
      if (key && Number.isFinite(region) && region > 0) {
        lookup.set(key, region);
      }
    });
    return lookup;
  }

  private getPanelMatchKeys(study: Study): string[] {
    const keys = new Set<string>();
    const addKey = (value?: string | null) => {
      if (!value) {
        return;
      }
      const normalized = this.normalizePanelAlias(value);
      if (normalized) {
        keys.add(normalized);
      }
    };

    addKey(study.panelSpecs?.kitname);
    addKey(study.panelSpecs?.kitname?.split(' ')[0]);
    addKey((study as { panelName?: string })?.panelName);

    return Array.from(keys);
  }

  private normalizePanelAlias(value?: string | null): string {
    const normalized = this.normalizePanelName(value);
    if (!normalized) {
      return '';
    }

    const aliasMap: Record<string, string> = {
      'apls-g': 'apls igg',
      'aplsg': 'apls igg',
      'vasculitis': 'vask',
      'vasc': 'vask',
      'vask': 'vask',
      'mmrv-g': 'mmrv igg',
      'mmrvg': 'mmrv igg',
      'lymet': 'lyme total',
      'lyme-t': 'lyme total',
      'ebv-m': 'ebv igm',
      'ebvm': 'ebv igm',
      'hsv-g': 'hsv igg',
      'hsvg': 'hsv igg',
      'ebv-g': 'ebv igg',
      'ebvg': 'ebv igg',
      'apls-m': 'apls igm',
      'aplsm': 'apls igm',
      'apls-a': 'apls iga',
      'aplsa': 'apls iga',
      'torc-g': 'torc igg',
      'torcg': 'torc igg',
      'ccp': 'anti-ccp',
      'celi-a': 'celiac iga',
      'celia': 'celiac iga',
      'celi-g': 'celiac igg',
      'celig': 'celiac igg'
    };

    return aliasMap[normalized] ?? normalized;
  }

  private normalizePanelName(value?: string | null): string {
    return (value ?? '').trim().toLowerCase();
  }

  transformVariableColumns(column: string): string {
    return transformToCamelCase(column);
  }

  trackBulkMaterial = (_: number, item: BulkMaterials): string =>
    // *MS Use a deterministic identifier so Angular can reuse dynamic column DOM nodes.*MS
    item.bulk_material_description?.trim() || item.part_number || `bulk-${_}`;

  ngOnDestroy(): void {
    this.destroyed = true;
    this.cancelScheduledTabActivation();
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    this.cdr.reattach();

    if (!this.isUpdated) {
      // *MS Persist edits only when pending changes have not yet been saved.*MS
      this.storeFormValues();
    }
  }

  private scheduleTabActivation(): void {
    // *MS Run Report Items renders the heaviest table, so we wait for the tab animation to finish before rehydrating it.*MS
    this.cancelScheduledTabActivation();
    const activate = () => {
      this.tabActivationHandle = null;
      this.tabActivationHandleIsTimeout = false;
      this.isTabActive = true;
      this.cdr.reattach();
      this.cdr.markForCheck();
      console.info('[RunReportItems] tabActivated', {
        pendingSelectedStudy: !!this.pendingSelectedStudy,
        pendingReportContent: !!this.pendingReportContent
      });
      if (this.pendingSelectedStudy) {
        this.applySelectedStudyData(this.pendingSelectedStudy);
        this.pendingSelectedStudy = null;
      }
      if (this.pendingReportContent) {
        this.applyReportContentResponse(this.pendingReportContent);
        this.pendingReportContent = null;
      }
    };

    if (this.shouldSkipAnimationDelay()) {
      activate();
      return;
    }

    if (typeof requestAnimationFrame === 'function') {
      this.tabActivationHandleIsTimeout = false;
      this.tabActivationHandle = requestAnimationFrame(activate);
      return;
    }

    this.tabActivationHandleIsTimeout = true;
    this.tabActivationHandle = setTimeout(activate, 0);
  }

  private cancelScheduledTabActivation(): void {
    // *MS If the user switches away again, cancel any pending frame so we don't waste work on a hidden tab.*MS
    if (this.tabActivationHandle === null) {
      return;
    }

    if (this.tabActivationHandleIsTimeout) {
      clearTimeout(this.tabActivationHandle as ReturnType<typeof setTimeout>);
    } else if (typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.tabActivationHandle as number);
    }

    this.tabActivationHandle = null;
  }

  private shouldSkipAnimationDelay(): boolean {
    // *MS Karma tests expect immediate execution; detect the runner and skip the animation delay there.*MS
    if (typeof window === 'undefined') {
      return false;
    }
    const win = window as typeof window & { __karma__?: unknown };
    return Boolean(win.__karma__);
  }

  private scheduleDeferredSave(saveFn: () => void): void {
    const run = () => {
      if (this.destroyed) {
        return;
      }
      saveFn();
    };

    if (typeof queueMicrotask === 'function') {
      queueMicrotask(run);
      return;
    }

    Promise.resolve().then(run);
  }

  private subscribeToReportContentResponse(): void {
    this.store.select(selectReportContentItemsSlice).pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged((previous, current) => this.areReportContentResponsesEqual(previous, current))
    ).subscribe(response => {
      if (!response) {
        return;
      }

      if (!this.isTabActive) {
        console.info('[RunReportItems] reportContent cached (tab inactive)', {
          reportContentCount: response?.reportContentItems?.length ?? 0,
          variableColumns: response?.variableColumns ?? []
        });
        // *MS Cache the payload while hidden so we avoid rebuilding the 100+ row form when the user is working in other tabs.*MS
        this.pendingReportContent = response;
        return;
      }

      this.applyReportContentResponse(response);
    });
  }

  private applyReportContentResponse(response: ReportContentItemResponse): void {
    // *MS Every report content change drives the FormArray. By storing the payload and calling loadReportContent only
    //   when study data is ready, we ensure the table reflects the latest variable columns without triggering extra
    //   MatTable re-renders.*MS
    this.reportContent = response;
    this.variableColumns = response?.variableColumns ?? [];
    console.info('[RunReportItems] applyReportContentResponse (pre-hydrate)', {
      reportContentCount: response?.reportContentItems?.length ?? 0,
      variableColumns: this.variableColumns,
      bulkMaterialsCount: this.bulkMaterial?.length ?? 0
    });
    if (this.selectedStudyData) {
      this.updateVariableColumnMeta();
      this.loadReportContent();
    }

    console.info('[RunReportItems] applyReportContentResponse', {
      reportContentCount: response?.reportContentItems?.length ?? 0,
      variableColumns: this.variableColumns,
      isTabActive: this.isTabActive,
      hasSelectedStudy: !!this.selectedStudyData
    });
  }

  private updateVariableColumnMeta(): void {
    const bulkMaterials = this.bulkMaterial ?? [];
    const bulkLookup = new Map<string, BulkMaterials>();
    bulkMaterials.forEach(material => {
      const ids = this.deriveBulkMaterialColumnIds(material.bulk_material_description || '');
      ids.forEach(id => {
        const key = this.normalizeVariableColumnId(id);
        if (key && !bulkLookup.has(key)) {
          bulkLookup.set(key, material);
        }
      });
    });

    const hasVariableColumns = (this.variableColumns ?? []).length > 0;
    const columnIds = hasVariableColumns
      ? this.variableColumns
      : bulkMaterials.map(material => this.transformVariableColumns(material.bulk_material_description));

    this.variableColumnMeta = columnIds.map(id => {
      const key = this.normalizeVariableColumnId(id);
      const match = key ? bulkLookup.get(key) : undefined;
      return {
        id,
        label: match?.bulk_material_description
          ? `${match.bulk_material_description} LN`
          : id,
        partNumber: match?.part_number ?? ''
      };
    });
  }

  private deriveBulkMaterialColumnIds(description: string): string[] {
    const ids = new Set<string>();
    const base = this.transformVariableColumns(description);
    if (base) {
      ids.add(base);
    }

    const stripped = description.replace(/["']/g, '').trim();
    if (stripped) {
      const tokens = stripped.split(/\s+/);
      if (tokens.length > 1 && tokens[tokens.length - 1].length === 1) {
        tokens.pop();
      }
      const cleaned = tokens.join(' ');
      if (cleaned) {
        const alt = this.transformVariableColumns(cleaned);
        if (alt) {
          ids.add(alt);
        }
      }
    }

    return Array.from(ids);
  }

  private normalizeVariableColumnId(value: string): string {
    return (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private buildPrintColumns(): void {
    const staticCols: ColumnSelection[] = REPORT_ITEM_COLUMNS.map(column => ({
      columnName: column.label,
      columnKey: column.columnKey,
      isSelected: true
    }));

    const variableCols: ColumnSelection[] = this.variableColumnMeta.map(m => ({
      columnName: m.label,
      columnKey: m.id,
      isSelected: true
    }));

    // Re-key any print selection onto the rebuilt columns. These are rebuilt fully selected, so
    // without this a rebuild — which the audit write on Print itself can cause — would send every
    // column to the printer however few were ticked, and re-tick the preview bar's checkboxes.
    this.tableColumns = applyPrintColumnSelection(
      [...staticCols, ...variableCols],
      this.printColumnSelection
    );
    this.selectedPrintColumns = this.tableColumns;
    if (this.isPrintMode) {
      this.printPagesSnapshot = this.buildPrintPages();
    }
  }

  private buildPrintPages(): ColumnSelection[][] {
    const anchorCols = this.selectedPrintColumns.filter(
      c => c.isSelected && this.anchorColumnKeys.includes(c.columnKey ?? '')
    );
    const nonAnchorCols = this.selectedPrintColumns.filter(
      c => c.isSelected && !this.anchorColumnKeys.includes(c.columnKey ?? '')
    );
    const dataColsPerPage = this.columnsPerPrintPage - anchorCols.length;
    if (dataColsPerPage <= 0 || !nonAnchorCols.length) {
      return anchorCols.length ? [anchorCols] : [];
    }
    const pages: ColumnSelection[][] = [];
    for (let i = 0; i < nonAnchorCols.length; i += dataColsPerPage) {
      pages.push([...anchorCols, ...nonAnchorCols.slice(i, i + dataColsPerPage)]);
    }
    return pages;
  }

  trackVariableColumn = (_: number, item: { id: string }): string => item.id;

  getPrintCellValue(rowIndex: number, columnKey: string): string {
    const group = this.reportContentFormArray?.at(rowIndex) as FormGroup | undefined;
    if (!group) {
      return '';
    }
    // A variable bulk-material column's id is its own form key, so it needs no mapping.
    const formKey = REPORT_ITEM_FORM_KEY_BY_COLUMN[columnKey] ?? columnKey;
    const value = group.get(formKey)?.value;
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    // The placeholder region reads as unselected on screen, so it prints as unset rather than as -1.
    if (formKey === 'region' && !this.isRealBeadRegion(value)) {
      return '-';
    }
    if (value instanceof Date) {
      return this.formatCouplingDate(value) || '-';
    }
    return String(value);
  }

  isPrintColumnSelected(columnKey: string): boolean {
    return this.selectedPrintColumns.some(c => c.columnKey === columnKey && c.isSelected);
  }

  private areReportContentResponsesEqual(
    previous: ReportContentItemResponse | null | undefined,
    current: ReportContentItemResponse | null | undefined
  ): boolean {
    // *MS Comparing entire deeply nested payloads is expensive. This equality check only inspects IDs and column
    //   structures, which lets us skip redundant hydrations when NgRx replays the same reference (the common case when
    //   other Run Info tabs dispatch unrelated actions).*MS
    if (previous === current) {
      return true;
    }
    const prevItems = previous?.reportContentItems ?? [];
    const currItems = current?.reportContentItems ?? [];
    if (prevItems.length !== currItems.length) {
      return false;
    }
    for (let i = 0; i < prevItems.length; i++) {
      if (prevItems[i]?.reportContentItemId !== currItems[i]?.reportContentItemId) {
        return false;
      }
    }
    return (previous?.variableColumns ?? []).join('|') === (current?.variableColumns ?? []).join('|');
  }

  private buildDisplayOrderFromCurrentTable(rows: ReportContentItem[]): CalAdjustedDisplayOrder {
    const kitLotOrder: string[] = [];
    const regionOrderByKitLot: Record<string, number[]> = {};
    const seenLots = new Set<string>();

    for (const r of rows) {
      const lot = String(r.lot ?? '');
      const region = Number(r.region);

      if (!lot) continue;

      if (!seenLots.has(lot)) {
        seenLots.add(lot);
        kitLotOrder.push(lot);
        regionOrderByKitLot[lot] = [];
      }

      if (!regionOrderByKitLot[lot].includes(region)) {
        regionOrderByKitLot[lot].push(region);
      }
    }

    return { kitLotOrder, regionOrderByKitLot };
  }
}
